/**
 * Badge "Solo online": verifica lazy per i prodotti multi-variante.
 *
 * Il Liquid (snippets/solo-online-check.liquid) decide da solo la maggior parte dei casi.
 * Quando ci sono varianti in stock di cui Shopify non espone store_availabilities, il badge
 * viene stampato nascosto con data-solo-online-variants="ID,ID" e data-solo-online-url (URL prodotto).
 * Qui, solo quando il badge sta per entrare nel viewport, si chiede per ogni variante la section
 * "solo-online-data" (/products/handle?variant=ID&section_id=solo-online-data).
 * Nota: /variants/ID non fornisce il contesto prodotto alla section, quindi si usa l'URL prodotto.
 * - basta una variante disponibile in un negozio con ritiro -> il badge resta nascosto;
 * - nessuna -> il badge viene mostrato.
 * In caso di errore il badge resta nascosto (meglio nessun badge che un badge sbagliato).
 */
(() => {
  const ATTR = 'data-solo-online-variants';
  const CACHE_PREFIX = 'solo-online:v1:';
  const CACHE_TTL = 10 * 60 * 1000; // 10 minuti
  const MAX_PARALLELO = 4;

  const inCorso = new Map(); // id variante -> Promise<boolean> (richieste condivise tra badge)
  const coda = [];
  let attive = 0;

  // --- Cache in sessionStorage (può non essere disponibile: sempre in try/catch) ---
  const leggiCache = (id) => {
    try {
      const raw = sessionStorage.getItem(CACHE_PREFIX + id);
      if (!raw) return null;
      const dato = JSON.parse(raw);
      if (Date.now() - dato.t > CACHE_TTL) return null;
      return dato.negozio;
    } catch (e) {
      return null;
    }
  };
  const scriviCache = (id, negozio) => {
    try {
      sessionStorage.setItem(CACHE_PREFIX + id, JSON.stringify({ t: Date.now(), negozio }));
    } catch (e) {
      /* storage pieno o bloccato: si lavora senza cache */
    }
  };

  // --- Coda con un massimo di richieste in parallelo ---
  const accoda = (lavoro) => new Promise((resolve, reject) => {
    coda.push({ lavoro, resolve, reject });
    avanza();
  });
  const avanza = () => {
    while (attive < MAX_PARALLELO && coda.length) {
      const { lavoro, resolve, reject } = coda.shift();
      attive += 1;
      lavoro().then(resolve, reject).finally(() => {
        attive -= 1;
        avanza();
      });
    }
  };

  // true = variante disponibile in almeno un negozio con ritiro
  const variantInNegozio = (url, id) => {
    const inCache = leggiCache(id);
    if (inCache !== null) return Promise.resolve(inCache);
    if (inCorso.has(id)) return inCorso.get(id);

    const indirizzo = new URL(url, window.location.origin);
    indirizzo.searchParams.set('variant', id);
    indirizzo.searchParams.set('section_id', 'solo-online-data');

    const richiesta = accoda(() => fetch(indirizzo.toString(), { credentials: 'same-origin' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nodo = doc.querySelector('[data-solo-online-store]');
        // La risposta deve riguardare proprio la variante richiesta
        if (!nodo || nodo.getAttribute('data-solo-online-variant') !== String(id)) {
          throw new Error('risposta non valida');
        }
        const negozio = nodo.getAttribute('data-solo-online-store') === '1';
        scriviCache(id, negozio);
        return negozio;
      }))
      .finally(() => inCorso.delete(id));

    inCorso.set(id, richiesta);
    return richiesta;
  };

  // Badge "Prezzo più basso in negozio" in attesa (data-store-price-pending) nella stessa card:
  // è escluso dal "Solo online", quindi si mostra solo se il "Solo online" non è confermato.
  const risolviPrezzoNegozio = (badge, soloOnline) => {
    const card = badge.closest('.product-block');
    if (!card) return;
    card.querySelectorAll('[data-store-price-pending]').forEach((el) => {
      el.removeAttribute('data-store-price-pending');
      el.hidden = soloOnline;
    });
  };

  // Verifica in sequenza: si ferma alla prima variante disponibile in negozio
  const verificaBadge = async (badge) => {
    const ids = (badge.getAttribute(ATTR) || '').split(',').filter(Boolean);
    const url = badge.getAttribute('data-solo-online-url');
    if (!ids.length || !url) return;
    let soloOnline = false;
    try {
      soloOnline = true;
      for (const id of ids) {
        if (await variantInNegozio(url, id)) { // in negozio -> "Solo online" resta nascosto
          soloOnline = false;
          break;
        }
      }
    } catch (e) {
      /* errore di rete o risposta inattesa: "Solo online" resta nascosto */
      soloOnline = false;
    }
    badge.hidden = !soloOnline;
    risolviPrezzoNegozio(badge, soloOnline);
  };

  // --- Osservazione lazy ---
  // Il badge è nascosto (display:none) e non ha dimensioni da intersecare:
  // si osserva il contenitore visibile più vicino (immagine della card o colonna prodotto).
  const visti = new WeakSet();
  const badgeDelContenitore = new WeakMap(); // contenitore -> badge da verificare
  const bersaglio = (badge) => badge.closest('.image-cont, .product-form, .product-block') || badge.parentElement;

  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        const badge = badgeDelContenitore.get(entry.target);
        if (badge) verificaBadge(badge);
      });
    }, { rootMargin: '200px 0px' })
    : null;

  const registraBadge = (badge) => {
    if (visti.has(badge)) return;
    visti.add(badge);
    if (!io) {
      verificaBadge(badge);
      return;
    }
    const target = bersaglio(badge);
    badgeDelContenitore.set(target, badge);
    io.observe(target);
  };

  const scansiona = (nodo) => {
    if (!nodo || nodo.nodeType !== 1) return;
    if (nodo.hasAttribute(ATTR)) registraBadge(nodo);
    nodo.querySelectorAll(`[${ATTR}]`).forEach(registraBadge);
  };

  const avvia = () => {
    scansiona(document.body);
    // Card aggiunte dopo il caricamento: infinite scroll, filtri, prodotti correlati, quick buy
    new MutationObserver((mutazioni) => {
      mutazioni.forEach((m) => m.addedNodes.forEach(scansiona));
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', avvia);
  } else {
    avvia();
  }
})();
